import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Youtube } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import Logo from "./Logo";

const Footer = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const res = await subscribeNewsletter(email);
            if (res.already_subscribed) {
                toast("You're already in the ranks.");
            } else {
                toast.success("Welcome to the fight. Check your inbox.");
            }
            setEmail("");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer
            className="bg-black border-t border-white/10 pb-20 lg:pb-16"
            data-testid="footer"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <div className="grid lg:grid-cols-3 gap-12">
                    <div>
                        <div className="flex items-center gap-3">
                            <Logo size={48} />
                            <div>
                                <div
                                    className="font-display text-2xl"
                                    style={{ letterSpacing: "0.18em" }}
                                >
                                    MINDWORKS
                                </div>
                                <div className="text-[10px] tracking-[0.3em] text-red-600 mt-0.5">
                                    THINK. FIGHT. TRANSFORM.
                                </div>
                            </div>
                        </div>
                        <p className="mt-6 text-white/60 max-w-sm leading-relaxed">
                            A battle cry for the generation silently losing the war in their minds.
                            Built from survival. Pointed toward hope.
                        </p>
                        <div className="mt-8 flex items-center gap-3">
                            <a
                                href="https://www.youtube.com/@MindWorksCo"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-11 h-11 border border-white/15 hover:border-red-600 hover:bg-red-700 transition-colors group"
                                data-testid="social-youtube"
                                aria-label="MindWorks on YouTube"
                            >
                                <Youtube size={18} className="text-white/70 group-hover:text-white" strokeWidth={1.5} />
                            </a>
                            <a
                                href="https://instagram.com/tim_anguiano"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-11 h-11 border border-white/15 hover:border-red-600 hover:bg-red-700 transition-colors group"
                                data-testid="social-instagram"
                                aria-label="Founder on Instagram"
                            >
                                <Instagram size={18} className="text-white/70 group-hover:text-white" strokeWidth={1.5} />
                            </a>
                            <div className="ml-2 text-[10px] tracking-[0.25em] uppercase text-white/40 font-stencil">
                                @MindWorksCo · @tim_anguiano
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="font-display tracking-[0.2em] text-sm text-white/90 uppercase">
                            Navigate
                        </div>
                        <ul className="mt-5 space-y-3 text-white/70">
                            <li><Link to="/story" className="hover:text-red-600 transition-colors" data-testid="footer-link-story">The Story</Link></li>
                            <li><Link to="/course" className="hover:text-red-600 transition-colors" data-testid="footer-link-course">Course</Link></li>
                            <li><Link to="/companion" className="hover:text-red-600 transition-colors" data-testid="footer-link-companion">Battlefield Companion</Link></li>
                            <li><Link to="/help" className="hover:text-red-600 transition-colors" data-testid="footer-link-help">Get Help Now</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div className="font-display tracking-[0.2em] text-sm text-white/90 uppercase">
                            Enlist
                        </div>
                        <p className="mt-5 text-white/60 text-sm">
                            Get raw, honest writing on the war for the mind. No fluff.
                        </p>
                        <form
                            onSubmit={handleSubmit}
                            className="mt-4 flex border border-white/20"
                            data-testid="newsletter-form"
                        >
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="flex-1 px-4 py-3 bg-black text-white placeholder-white/30 outline-none focus:border-red-600 text-sm"
                                data-testid="newsletter-email-input"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-5 bg-red-700 hover:bg-red-800 text-white font-display tracking-[0.15em] text-xs uppercase transition-colors disabled:opacity-50"
                                data-testid="newsletter-submit"
                            >
                                {loading ? "..." : "Join"}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
                    <div>© {new Date().getFullYear()} MindWorks. Take every thought captive.</div>
                    <div className="font-stencil tracking-widest">
                        2 CORINTHIANS 10:5
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
